// ============================================================================
// KLINISOL CRO - SOP Upload Form Component (REDESIGNED)
// Pure Tailwind CSS - Matches Dashboard/Navbar design system
// Super Admin Only
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import ESignatureModal from './ESignatureModal';
import { 
    Upload, 
    FileText, 
    Calendar, 
    Tag, 
    User, 
    AlertTriangle,
    FolderOpen,
    Hash,
    Clock,
    FileCheck,
    Info
} from 'lucide-react';

const SOPUploadForm = ({ onSuccess }) => {
    const { user } = useAuth();
    const [categories, setCategories] = useState([]);
    const [formData, setFormData] = useState({
        sopNumber: '',
        title: '',
        category: '',
        version: '1.0',
        effectiveDate: '',
        reviewDate: '',
        owner: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
        description: '',
        keywords: ''
    });
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showSignatureModal, setShowSignatureModal] = useState(false);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await api.get('/sops/categories');
            if (response.data.success) {
                setCategories(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            // Validate file type
            const validTypes = ['application/pdf', 'text/html'];
            const validExtensions = ['.pdf', '.html', '.htm'];
            const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

            if (!validTypes.includes(selectedFile.type) && !validExtensions.includes(fileExtension)) {
                setErrors(prev => ({
                    ...prev,
                    file: 'Invalid file type. Only PDF and HTML files are allowed.'
                }));
                setFile(null);
                setFileName('');
                return;
            }

            // Validate file size (50MB)
            const maxSize = 50 * 1024 * 1024;
            if (selectedFile.size > maxSize) {
                setErrors(prev => ({
                    ...prev,
                    file: 'File size exceeds 50MB limit.'
                }));
                setFile(null);
                setFileName('');
                return;
            }

            setFile(selectedFile);
            setFileName(selectedFile.name);
            setErrors(prev => ({
                ...prev,
                file: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.sopNumber.trim()) {
            newErrors.sopNumber = 'SOP Number is required';
        }

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!formData.category) {
            newErrors.category = 'Category is required';
        }

        if (!formData.version.trim()) {
            newErrors.version = 'Version is required';
        }

        if (!formData.effectiveDate) {
            newErrors.effectiveDate = 'Effective Date is required';
        }

        if (!formData.reviewDate) {
            newErrors.reviewDate = 'Review Date is required';
        }

        if (!file) {
            newErrors.file = 'Please select a file to upload';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm()) {
            setShowSignatureModal(true);
        }
    };

    const handleSignature = async (signatureData) => {
        try {
            setLoading(true);

            // Create FormData
            const uploadData = new FormData();
            uploadData.append('sopFile', file);
            uploadData.append('sopNumber', formData.sopNumber);
            uploadData.append('title', formData.title);
            uploadData.append('category', formData.category);
            uploadData.append('version', formData.version);
            uploadData.append('effectiveDate', formData.effectiveDate);
            uploadData.append('reviewDate', formData.reviewDate);
            uploadData.append('owner', formData.owner);
            uploadData.append('description', formData.description);
            uploadData.append('keywords', formData.keywords);
            uploadData.append('pin', signatureData.pin);
            uploadData.append('signatureMeaning', signatureData.signatureMeaning);
            if (signatureData.comments) {
                uploadData.append('comments', signatureData.comments);
            }

            const response = await api.post('/sops/upload', uploadData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                // Reset form
                setFormData({
                    sopNumber: '',
                    title: '',
                    category: '',
                    version: '1.0',
                    effectiveDate: '',
                    reviewDate: '',
                    owner: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
                    description: '',
                    keywords: ''
                });
                setFile(null);
                setFileName('');
                setErrors({});
                setShowSignatureModal(false);

                // Call success callback
                if (onSuccess) {
                    onSuccess(response.data.data);
                }

                alert('SOP uploaded successfully!');
            }

        } catch (err) {
            console.error('Error uploading SOP:', err);
            const errorMessage = err.response?.data?.message || 'Failed to upload SOP. Please try again.';
            alert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* SOP Number */}
                <div>
                    <label htmlFor="sopNumber" className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                        <Hash className="w-4 h-4 text-[#475569]" />
                        SOP Number <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="sopNumber"
                        name="sopNumber"
                        value={formData.sopNumber}
                        onChange={handleInputChange}
                        placeholder="e.g., SOP-QMS-001"
                        className={`w-full px-4 py-3 border-2 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:bg-white focus:ring-4 ${
                            errors.sopNumber 
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/10' 
                                : 'border-gray-200 focus:border-[#475569] focus:ring-[#475569]/10'
                        }`}
                    />
                    {errors.sopNumber && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            {errors.sopNumber}
                        </p>
                    )}
                </div>

                {/* Title */}
                <div>
                    <label htmlFor="title" className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                        <FileText className="w-4 h-4 text-[#475569]" />
                        Title <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="e.g., Quality Management System Overview"
                        className={`w-full px-4 py-3 border-2 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:bg-white focus:ring-4 ${
                            errors.title 
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500/10' 
                                : 'border-gray-200 focus:border-[#475569] focus:ring-[#475569]/10'
                        }`}
                    />
                    {errors.title && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            {errors.title}
                        </p>
                    )}
                </div>

                {/* Category and Version */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="category" className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                            <FolderOpen className="w-4 h-4 text-[#475569]" />
                            Category <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="category"
                            name="category"
                            value={formData.category}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 border-2 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:bg-white focus:ring-4 ${
                                errors.category 
                                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/10' 
                                    : 'border-gray-200 focus:border-[#475569] focus:ring-[#475569]/10'
                            }`}
                        >
                            <option value="">Select Category</option>
                            {categories.map((cat) => (
                                <option key={cat.category} value={cat.category}>
                                    {cat.category}
                                </option>
                            ))}
                        </select>
                        {errors.category && (
                            <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {errors.category}
                            </p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="version" className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                            <Tag className="w-4 h-4 text-[#475569]" />
                            Version <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="version"
                            name="version"
                            value={formData.version}
                            onChange={handleInputChange}
                            placeholder="e.g., 1.0"
                            className={`w-full px-4 py-3 border-2 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:bg-white focus:ring-4 ${
                                errors.version 
                                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/10' 
                                    : 'border-gray-200 focus:border-[#475569] focus:ring-[#475569]/10'
                            }`}
                        />
                        {errors.version && (
                            <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {errors.version}
                            </p>
                        )}
                    </div>
                </div>

                {/* Effective Date and Review Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="effectiveDate" className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                            <Calendar className="w-4 h-4 text-[#475569]" />
                            Effective Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            id="effectiveDate"
                            name="effectiveDate"
                            value={formData.effectiveDate}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 border-2 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:bg-white focus:ring-4 ${
                                errors.effectiveDate 
                                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/10' 
                                    : 'border-gray-200 focus:border-[#475569] focus:ring-[#475569]/10'
                            }`}
                        />
                        {errors.effectiveDate && (
                            <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {errors.effectiveDate}
                            </p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="reviewDate" className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                            <Clock className="w-4 h-4 text-[#475569]" />
                            Review Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            id="reviewDate"
                            name="reviewDate"
                            value={formData.reviewDate}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 border-2 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:bg-white focus:ring-4 ${
                                errors.reviewDate 
                                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/10' 
                                    : 'border-gray-200 focus:border-[#475569] focus:ring-[#475569]/10'
                            }`}
                        />
                        {errors.reviewDate && (
                            <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {errors.reviewDate}
                            </p>
                        )}
                    </div>
                </div>

                {/* Owner */}
                <div>
                    <label htmlFor="owner" className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                        <User className="w-4 h-4 text-gray-600" />
                        Owner <span className="text-gray-500 text-xs">(Optional)</span>
                    </label>
                    <input
                        type="text"
                        id="owner"
                        name="owner"
                        value={formData.owner}
                        onChange={handleInputChange}
                        placeholder="SOP Owner Name"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:border-[#475569] focus:bg-white focus:ring-4 focus:ring-[#475569]/10"
                    />
                </div>

                {/* Description */}
                <div>
                    <label htmlFor="description" className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                        <FileText className="w-4 h-4 text-gray-600" />
                        Description <span className="text-gray-500 text-xs">(Optional)</span>
                    </label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Brief description of the SOP..."
                        rows="3"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:border-[#475569] focus:bg-white focus:ring-4 focus:ring-[#475569]/10 resize-vertical"
                    />
                </div>

                {/* Keywords */}
                <div>
                    <label htmlFor="keywords" className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                        <Tag className="w-4 h-4 text-gray-600" />
                        Keywords <span className="text-gray-500 text-xs">(Optional)</span>
                    </label>
                    <input
                        type="text"
                        id="keywords"
                        name="keywords"
                        value={formData.keywords}
                        onChange={handleInputChange}
                        placeholder="e.g., quality, management, process (comma-separated)"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-base transition-all bg-gray-50 focus:outline-none focus:border-[#475569] focus:bg-white focus:ring-4 focus:ring-[#475569]/10"
                    />
                </div>

                {/* File Upload */}
                <div>
                    <label htmlFor="file" className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-2">
                        <FileCheck className="w-4 h-4 text-[#475569]" />
                        SOP Document <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <input
                            type="file"
                            id="file"
                            accept=".pdf,.html,.htm"
                            onChange={handleFileChange}
                            className="hidden"
                        />
                        <label 
                            htmlFor="file" 
                            className={`flex items-center justify-center gap-2 w-full px-6 py-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                                errors.file
                                    ? 'border-red-500 bg-red-50 hover:bg-red-100'
                                    : fileName
                                        ? 'border-green-500 bg-green-50 hover:bg-green-100'
                                        : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-[#475569]'
                            }`}
                        >
                            <Upload className={`w-5 h-5 ${fileName ? 'text-green-600' : 'text-gray-600'}`} />
                            <span className={`font-semibold ${fileName ? 'text-green-900' : 'text-gray-700'}`}>
                                {fileName || 'Choose File'}
                            </span>
                        </label>
                        {fileName && (
                            <div className="mt-2 flex items-center gap-2 text-sm text-gray-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                                <FileCheck className="w-4 h-4 text-blue-600" />
                                <span className="font-medium">{fileName}</span>
                            </div>
                        )}
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                        <Info className="w-3 h-3" />
                        <span>Supported formats: PDF, HTML (Max 50MB)</span>
                    </div>
                    {errors.file && (
                        <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            {errors.file}
                        </p>
                    )}
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                    <button 
                        type="submit" 
                        className="w-full px-8 py-4 bg-gradient-to-r from-[#475569] to-[#334155] text-white rounded-xl text-lg font-bold hover:-translate-y-0.5 transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        disabled={loading}
                    >
                        <Upload className="w-6 h-6" />
                        <span>Upload SOP</span>
                    </button>
                </div>
            </form>

            {/* E-Signature Modal */}
            <ESignatureModal
                isOpen={showSignatureModal}
                onClose={() => setShowSignatureModal(false)}
                onSign={handleSignature}
                actionType="authorship_upload"
                actionDescription={`Upload SOP: ${formData.sopNumber} - ${formData.title}`}
                loading={loading}
            />
        </div>
    );
};

export default SOPUploadForm;